import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Clock, Share2, Wifi, Coffee, BatteryCharging, Camera, Shield, TrendingUp, CheckCircle } from "lucide-react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import EzoicAd from "../components/EzoicAd";
import { GUIDES } from "./GuidesPage";

const iconMap = {
  "wifi-requirements": Wifi,
  "workspace-etiquette": Coffee,
  "finding-power-outlets": BatteryCharging,
  "video-call-locations": Camera,
  "security-public-wifi": Shield,
  "productivity-tips": TrendingUp
};

export default function GuideDetailPage() {
  const { guideId } = useParams();
  const guide = GUIDES.find(g => g.id === guideId);

  if (!guide) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-4xl mx-auto px-4 py-20 text-center">
          <h1 className="text-2xl font-bold text-gray-900 mb-4">Guide Not Found</h1>
          <p className="text-gray-600 mb-8">The guide you're looking for doesn't exist.</p>
          <Link to="/guides" className="text-teal-600 font-medium hover:underline">
            ← Back to all guides
          </Link>
        </div>
        <Footer />
      </div>
    );
  }

  const IconComponent = iconMap[guide.id] || CheckCircle;

  return (
    <div className="min-h-screen bg-gray-50">
      <SEO 
        title={`${guide.title} | Strider Remote Work Guides`}
        description={guide.description}
        keywords={`${guide.title?.toLowerCase() || 'guide'}, remote work, UK workspace, strider guide`}
        canonical={`/guides/${guide.id}`}
      />
      <Header />
      
      {/* Article Header */}
      <article className="max-w-4xl mx-auto px-4 py-8">
        {/* Back Link */}
        <Link 
          to="/guides" 
          className="inline-flex items-center gap-2 text-gray-500 hover:text-teal-600 mb-8 text-sm"
        >
          <ArrowLeft className="w-4 h-4" />
          Back to all guides
        </Link>

        {/* Header */}
        <header className="mb-10">
          <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-6 bg-teal-50 text-teal-600`}>
            <IconComponent className="w-8 h-8" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
            {guide.title}
          </h1>
          <p className="text-xl text-gray-600 mb-6">
            {guide.description}
          </p>
          <div className="flex items-center gap-4 text-sm text-gray-500">
            <span className="flex items-center gap-1">
              <Clock className="w-4 h-4" />
              {guide.readTime}
            </span>
            <span>|</span>
            <span>Strider Editorial Team</span>
          </div>
        </header>

        {/* Content */}
        <div 
          className="prose prose-lg max-w-none prose-headings:text-gray-900 prose-p:text-gray-600 prose-li:text-gray-600 prose-strong:text-gray-900 prose-a:text-teal-600"
          dangerouslySetInnerHTML={{ __html: guide.content }}
        />

        {/* Ezoic Ad - In-content placement */}
        <div className="my-8">
          <EzoicAd placementId={106} />
        </div>

        {/* Related Guides */}
        <section className="mt-16 pt-10 border-t border-gray-200">
          <h2 className="text-xl font-bold text-gray-900 mb-6">Related Guides</h2>
          <div className="grid md:grid-cols-2 gap-4">
            {GUIDES.filter(g => g.id !== guide.id).slice(0, 2).map(relatedGuide => (
              <Link 
                key={relatedGuide.id}
                to={`/guides/${relatedGuide.id}`}
                className="block p-4 bg-white rounded-lg border border-gray-200 hover:border-teal-300 hover:shadow-md transition-all"
              >
                <h3 className="font-semibold text-gray-900 mb-1">{relatedGuide.title}</h3>
                <p className="text-sm text-gray-500">{relatedGuide.readTime}</p>
              </Link>
            ))}
          </div>
        </section>

        {/* CTA */}
        <section className="mt-12 p-6 bg-teal-50 rounded-xl border border-teal-100">
          <h2 className="text-lg font-bold text-gray-900 mb-2">Let's expand where the world can succeed.</h2>
          <p className="text-gray-600 mb-4">
            Browse verified workspaces with real WiFi speeds, power availability, and user reviews.
          </p>
          <Link 
            to="/browse"
            className="inline-flex items-center gap-2 px-4 py-2 bg-teal-600 text-white font-medium rounded-lg hover:bg-teal-700 transition-colors"
          >
            Browse Workspaces →
          </Link>
        </section>
      </article>

      <Footer />
    </div>
  );
}
